import { RolodexDocumentEntry } from 'features/files/types'
import {
  isImageDocument,
  isPdfDocument,
} from 'features/files/utils/DocumentUtils'
import { normalizeDocumentCategories } from 'features/files/utils/normalizeDocumentCategories'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryTagsType, RolodexEntryType } from 'types'
import { z } from 'zod'

const optionalString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional()
)

const categoriesArray = z.preprocess((value) => {
  if (Array.isArray(value)) {
    return normalizeDocumentCategories(value)
  }

  if (typeof value === 'string' && value.length > 0) {
    return normalizeDocumentCategories([value])
  }

  return []
}, z.array(z.string()))

const documentEntryDataSchema = z
  .object({
    filename: optionalString,
    size: z.preprocess((value) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value
      }
      if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10)
        return Number.isFinite(parsed) ? parsed : undefined
      }
      return undefined
    }, z.number().optional()),
    file_type: optionalString,
    categories: categoriesArray,
    upload_date: optionalString,
    linked_entry_id: optionalString,
    fields: z.preprocess(
      (value) =>
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : undefined,
      z.record(z.string(), z.unknown()).optional()
    ),
    visible_name: optionalString,
    notes: optionalString,
  })
  .passthrough()
/**
 * Process document entries into Document objects
 */
export const processDocumentEntries = (
  documentEntries: ReadonlyArray<GeckoEntry>
): ReadonlyArray<RolodexDocumentEntry> => {
  return documentEntries.map((entry: GeckoEntry) => {
    const data = documentEntryDataSchema.parse(entry.data ?? {})

    const filename =
      typeof data.filename === 'string' && data.filename.length > 0
        ? data.filename
        : 'Unknown file'

    const fileType =
      typeof data.file_type === 'string' && data.file_type.length > 0
        ? data.file_type
        : typeof data.type === 'string' && data.type.length > 0
          ? data.type
          : 'unknown'

    // TODO(talexander): remove this once the docs are ported over
    const legacyCategory =
      typeof data.category === 'string' && data.category.length > 0
        ? data.category
        : undefined

    const normalizedCategories =
      data.categories.length > 0
        ? data.categories
        : legacyCategory
          ? normalizeDocumentCategories([legacyCategory])
          : []

    return {
      type: RolodexEntryType.REPORT,
      entry_id: entry.id,
      filename,
      size: data.size,
      file_type: fileType,
      categories: normalizedCategories,
      upload_date: data.upload_date,
      linked_entry_id: data.linked_entry_id ?? '',
      fields: data.fields ?? {},
      visible_name: data.visible_name,
      notes: data.notes,
      tags: entry.tags,
    }
  })
}

export const useLinkedDocuments = (
  tags: RolodexEntryTagsType | undefined
): {
  documents: ReadonlyArray<RolodexDocumentEntry>
  isLoading: boolean
  hasEverLoaded: boolean
  documentEntries: ReadonlyArray<GeckoEntry>
  forceRefresh: () => Promise<void>
} => {
  const queryParams = useMemo(
    () =>
      tags
        ? [
            {
              type: RolodexEntryType.REPORT,
              tags,
            },
          ]
        : undefined,
    [tags]
  )

  const {
    entries: documentEntries,
    isLoading: documentsLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryParams)

  // Fetch file references for all documents at once

  // Process document entries into Document objects with file references
  const documents = useMemo(() => {
    return processDocumentEntries(documentEntries)
  }, [documentEntries])

  return useMemo(
    () => ({
      documents,
      isLoading: documentsLoading,
      hasEverLoaded,
      documentEntries,
      forceRefresh,
    }),
    [documents, documentsLoading, documentEntries, forceRefresh, hasEverLoaded]
  )
}

/**
 * Hook to get signed URLs for document previews
 */
export const useDocumentSignedUrls = (
  document: RolodexDocumentEntry | null
) => {
  const isPdf = useMemo(() => isPdfDocument(document), [document])
  const isImage = useMemo(() => isImageDocument(document), [document])

  const urlParams = useMemo(
    () =>
      document && (isPdf || isImage) && document.filename
        ? [{ entry_id: document.entry_id, path: document.filename }]
        : undefined,
    [document, isPdf, isImage]
  )

  const { urls: signedUrls, isLoading: urlsLoading } =
    useRolodexSignedURLs(urlParams)

  return {
    signedUrl: signedUrls.length > 0 ? signedUrls[0] : undefined,
    isLoading: urlsLoading,
    isPdf,
    isImage,
  }
}
